import React, { useEffect, useState, useRef } from "react";
import "./AccountHolder.css";
import MainLayout from "components/layout/main-layout/MainLayout";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import { titles } from "constants/titles";
import CustomInput from "components/widgets/custom-input/CustomInput";
import CustomButton from "components/widgets/custom-button/CustomButton";
import CustomSelectBox from "components/widgets/custom-select-box/CustomSelectBox";
import {
  IconQuestion,
  IconTrash,
  IconEdit,
  DragHandleIcon,
} from "assets/SVGs/SVGs";
import {
  getPhoneTypeList,
  getRegionList,
  setSelectedPhoneType,
} from "redux/Common/action";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  formatPhoneNumber,
  getAllRegionListNames,
  getDataFromId,
} from "utility";
import ChangesSavedModal from "components/layout/changes-saved-modal/ChangesSavedModal";
import InformationModal from "components/layout/information-modal/InformationModal";
import ConfirmationModal from "components/layout/confirmation-modal/ConfirmationModal";
import AddAccountContactModal from "components/layout/add-account-contact-modal/AddAccountContactModal";
import { ROUTES_CONSTANTS } from "routes/route-constants";
import { sub_profile, curv } from "assets/images/images";
import { FIELD_CONFIG, VALIDATION_FIELDS } from "./config";
import * as helpers from "./helpers";

// Constants for repetitive usage
const ICON_PROPS = {
  questionIcon: {
    fillColor: "var(--blackText)",
    width: "24",
    height: "24"
  },
  actionIcon: {
    color: "#595252"
  }
};

const AccountHolder = () => {
  const [accountHolderState, setAccountHolderState] = useState({
    email: "",
    street: "",
    zip_code: "",
    city: "",
    apt: "",
    state: "",
    loading: false,
  });
  const [deleteContactModal, setDeleteContactModal] = useState(false);
  const [changesSaveModal, setChangesSaveModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [newContact, setNewContact] = useState({
    phoneNumber: "",
    type: "",
    type_id: "",
    auto_notify: false,
  });
  const [infoModalPopup, setInfoModalPopup] = useState();

  // Mobile-like validation states
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [errorState, setErrorState] = useState({
    show: false,
    fieldErrors: { ...VALIDATION_FIELDS },
    fieldMessages: { ...VALIDATION_FIELDS }
  });

  // Scroll handling for snackbar animation (exactly like mobile)
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    // Find the layout_container which is the actual scrolling element
    const scrollContainer = document.querySelector('.layout_container');

    const handleScroll = (e) => {
      const currentScrollY = e.target.scrollTop || 0;
      setScrollY(currentScrollY);
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const snackbarTop = helpers.calculateSnackbarTop(scrollY);

  // Use advanced validation hook for MessageSnackBar
  const {
    snackbarState,
    dismissSnackbar,
    onApiResponse,
    handleApiResponse,
  } = useAdvancedValidation(FIELD_CONFIG);

  const { phoneTypeList = [] } = useSelector(
    (store) => store?.common
  );
  const allRegionList = useSelector((store) => store?.common?.allRegionList);
  const { accountHolderData, loading } = useSelector(
    (store) => store?.userDetail
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const [phoneList, setPhoneList] = useState([]);
  const previousAccountDataRef = useRef(null);

  useEffect(() => {
    getRegionList({
      dispatch,
      userType: "accountowner",
    });
    getPhoneTypeList({
      dispatch,
      history,
      userType: "accountowner",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accountHolderData?.firstname?.length > 0 && phoneTypeList?.length > 0) {
      // Check if accountHolderData has actually changed (by comparing phone numbers)
      const hasDataChanged =
        previousAccountDataRef.current?.phone_one !== accountHolderData?.phone_one ||
        previousAccountDataRef.current?.phone_two !== accountHolderData?.phone_two ||
        previousAccountDataRef.current === null;

      if (hasDataChanged) {
        setAccountHolderState((prevState) => ({
          ...prevState,
          email: accountHolderData?.email ?? "",
          street: accountHolderData?.street,
          apt: accountHolderData?.apt_no,
          city: accountHolderData?.city ?? "",
          state: helpers.getRegionName(accountHolderData?.region_id, allRegionList),
          zip_code: accountHolderData?.post_code ?? "",
        }));
        let cloneData = [];
        const phoneType1 = getDataFromId(
          phoneTypeList,
          "phone_type_id",
          accountHolderData?.phone_type_one
        );
        const phoneType2 = getDataFromId(
          phoneTypeList,
          "phone_type_id",
          accountHolderData?.phone_type_two
        );
        if (accountHolderData?.phone_one) {
          cloneData?.push({
            phoneNumber: accountHolderData?.phone_one,
            id: 0,
            type: phoneType1 && phoneType1?.length > 0 ? phoneType1[0] : "",
            type_id: phoneType1 && phoneType1?.length > 0 ? phoneType1[0]?.phone_type_id : "",
            auto_notify:
              accountHolderData?.is_phone_one_auto_notify === 0 ? true : false,
          });
        }
        if (accountHolderData?.phone_two) {
          cloneData?.push({
            phoneNumber: accountHolderData?.phone_two,
            id: 1,
            type: phoneType2 && phoneType2?.length > 0 ? phoneType2[0] : "",
            type_id: phoneType2 && phoneType2?.length > 0 ? phoneType2[0]?.phone_type_id : "",
            auto_notify:
              accountHolderData?.is_phone_two_auto_notify === 0 ? true : false,
          });
        }
        setPhoneList(cloneData);
        previousAccountDataRef.current = accountHolderData;
      }
    }
  }, [accountHolderData, allRegionList, phoneTypeList]);


  const onValueChange = (type, value) => {
    helpers.handleValueChange(type, value, {
      accountHolderState,
      setAccountHolderState,
      validationTriggered,
      errorState,
      setErrorState,
      snackbarState,
      onApiResponse,
      dismissSnackbar
    });
  };

  const handleStateChange = (option) => {
    onValueChange("state", option?.value);
  };

  const handleNameEmailHelp = () => {
    showModal(titles?.modal_texts?.name_email_help_tip);
  };

  const handleMailingAddressHelp = () => {
    showModal(titles?.modal_texts?.mail_address_help_tip);
  };

  const handlePhoneNumberHelp = () => {
    showModal(titles?.modal_texts?.phone_no_help_tip);
  };

  const handleEditContactClick = (contact) => {
    onEditContact(contact);
  };

  const handleDeleteContactClick = (contact) => {
    onDeleteContact(contact);
  };

  const handleDragStart = (e, index) => {
    e?.dataTransfer?.setData("text/plain", index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e?.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"));

    if (dragIndex !== dropIndex) {
      const newPhoneList = [...phoneList];
      const draggedItem = newPhoneList[dragIndex];
      newPhoneList?.splice(dragIndex, 1);
      newPhoneList?.splice(dropIndex, 0, draggedItem);
      setPhoneList(newPhoneList);
    }
  };


  const handleAddContactModalClose = () => {
    setShowAddContactModal(false);
    setEditingContact(null);
    setSelectedPhoneType(dispatch, null);
  };

  const onSubmit = async () => {
    await helpers.handleSubmit({
      accountHolderState,
      phoneList,
      phoneTypeList,
      accountHolderData,
      allRegionList,
      setValidationTriggered,
      setErrorState,
      onApiResponse,
      dispatch,
      history,
      handleApiResponse,
      setChangesSaveModal,
      VALIDATION_FIELDS
    });
  };

  const onAddContact = () => {
    setEditingContact(null); // Clear editing contact
    setSelectedPhoneType(dispatch, null); // Clear selected phone type in Redux
    setShowAddContactModal(true);
    setNewContact({
      phoneNumber: "",
      type: "",
      type_id: "",
      auto_notify: false,
    });
  };

  const onEditContact = (contact) => {
    // Set the phone type in Redux BEFORE opening the modal (like mobile app)
    setSelectedPhoneType(dispatch, contact?.type);
    setEditingContact(contact);
    setNewContact({
      phoneNumber: contact?.phoneNumber,
      type: contact?.type,
      type_id: contact?.type_id,
      auto_notify: contact?.auto_notify,
    });
    setShowAddContactModal(true);
  };

  // Handle phone list changes and clear phoneList error
  const handlePhoneListChange = (newPhoneList) => {
    helpers.handlePhoneListChange(newPhoneList, {
      setPhoneList,
      validationTriggered,
      errorState,
      setErrorState,
      snackbarState,
      onApiResponse,
      dismissSnackbar,
      VALIDATION_FIELDS
    });
  };

  const onSaveContact = (contactData) => {
    let updatedList;

    const typeId = contactData?.type?.phone_type_id || contactData?.type_id;
    if (editingContact) {
      // Update existing contact
      updatedList = phoneList?.map((contact) =>
        contact.id === editingContact?.id
          ? {
            ...contact,
            phoneNumber: contactData?.phoneNumber,
            type: contactData?.type,
            type_id: typeId,
            auto_notify: contactData?.isAcceptSignature,
          }
          : contact
      );
    } else {
      // Add new contact
      const newContactWithId = {
        id: phoneList.length,
        phoneNumber: contactData?.phoneNumber,
        type: contactData?.type,
        type_id: typeId,
        auto_notify: contactData?.isAcceptSignature,
      };
      updatedList = [...phoneList, newContactWithId];
    }

    handlePhoneListChange(updatedList);
    setShowAddContactModal(false);
    setEditingContact(null);
    setSelectedPhoneType(dispatch, null);
    setNewContact({
      phoneNumber: "",
      type: "",
      type_id: "",
      auto_notify: false,
    });
  };

  const onDeleteContact = (contactToDelete) => {
    setDeleteContactModal(contactToDelete);
  };

  const confirmDeleteContact = () => {
    if (deleteContactModal) {
      const updatedList = phoneList.filter(
        (contact) => contact.id !== deleteContactModal.id
      );
      handlePhoneListChange(updatedList);
      if (editingContact && editingContact.id === deleteContactModal.id) {
        setShowAddContactModal(false);
        setEditingContact(null);
        setSelectedPhoneType(dispatch, null);
      }
      setDeleteContactModal(false);
    }
  };


  const onContactUs = () => {
    history.push(ROUTES_CONSTANTS?.helpCenter);
  };

  const showModal = (description) => {
    setInfoModalPopup({
      description,
      isVisible: true,
    });
  };


  // Check if there are any validation errors
  const hasValidationErrors = Object.values(errorState.fieldErrors).some(error => error);

  // Don't auto-close snackbar if there are validation errors
  const snackbarAutoHideDuration = hasValidationErrors && validationTriggered
    ? 0
    : snackbarState?.autoHideDuration;

  return (
    <MainLayout
      text={titles?.edit_account_holder}
      isBackHide={false}
      isNavbar={true}
      white={true}
      fullWidthScreen={true}
      isLoading={loading}
    >

      {/* Modals */}
      <ConfirmationModal
        show={!!deleteContactModal}
        setShow={setDeleteContactModal}
        svgIcon={<IconTrash color={'#DB4E3B'} />}
        title={titles?.delete_contact}
        description={titles?.are_you_sure_to_remove_number}
        btnText={titles?.remove}
        onClick={confirmDeleteContact}
      />

      <ChangesSavedModal
        show={changesSaveModal}
        setShow={setChangesSaveModal}
      />

      <InformationModal
        show={infoModalPopup?.isVisible}
        setShow={setInfoModalPopup}
        infoText={infoModalPopup?.description}
      />
      <div className="ah_container">
        {/* Profile Container - Following AccountHolderHome exact structure */}
        <div className="ah_profile_container">
          {/* Profile Section */}
          <div className="ah_profile_section">
            <div className="ah_profile_image_container">
              <img
                src={sub_profile}
                alt="Profile"
                className="ah_profile_image"
              />
            </div>
            <div className="ah_profile_text">
              <h2 className="display_h1 ah_profile_name">
                {accountHolderData?.firstname +
                  " " +
                  accountHolderData?.lastname}
              </h2>
              <p className="ah_profile_email">{accountHolderData?.email}</p>
            </div>
          </div>
        </div>

        {/* Curved Bottom - Separate from container like AccountHolderHome */}
        <div className="ah_curved_container">
          <img src={curv} alt="Curved bottom" className="ah_curved_image" />
        </div>

        {/* Animated MessageSnackBar - positioned outside content like mobile (outside ScrollView) */}
        {snackbarState?.show && (
          <div
            className="ah_snackbar_animated"
            style={{
              top: `${snackbarTop}px`
            }}
          >
            <MessageSnackBar
              message={snackbarState?.message}
              isVisible={true}
              type={snackbarState?.type}
              autoHideDuration={snackbarAutoHideDuration}
              constrainWidth={false}
              onDismiss={dismissSnackbar}
            />
          </div>
        )}

        {/* Content */}
        <div className="ah_content">
          {/* Placeholder for snackbar - reserves space like mobile */}
          {snackbarState?.show && <div className="ah_snackbar_placeholder" />}

          {/* Name & Email Section */}
          <div className="ah_section">
            <div className="ah_section_header">
              <h3 className="ah_section_title">{titles?.name_email}</h3>
              <IconQuestion
                className="ah_question_button"
                onClick={handleNameEmailHelp}
                {...ICON_PROPS.questionIcon}
              />
            </div>
            <p className="ah_section_description">
              {titles?.please_call_to_name_email}{" "}
              <span className="ah_contact_link" onClick={onContactUs}>
                {titles?.contact_us}
              </span>
            </p>
            <div className="ah_input_group">
              <CustomInput
                placeholder={`${accountHolderData?.firstname +
                  " " +
                  accountHolderData?.lastname
                  }*`}
                disabled={true}
                className="ah_disabled_input"
              />
              <CustomInput
                placeholder={`${accountHolderData?.email}*`}
                disabled={true}
                className="ah_disabled_input"
              />
            </div>
          </div>

          {/* Mailing Address Section */}
          <div className="ah_section">
            <div className="ah_section_header">
              <h3 className="ah_section_title">{titles?.mailing_address}</h3>
              <IconQuestion
                className="ah_question_button"
                onClick={handleMailingAddressHelp}
                {...ICON_PROPS.questionIcon}
              />
            </div>
            <div className="ah_input_group">
              <CustomInput
                type="text"
                required
                placeholder={titles?.street}
                value={accountHolderState?.street}
                error={validationTriggered && errorState.fieldErrors?.street && errorState.show}
                errorMessage={validationTriggered && errorState.fieldErrors?.street && errorState.show ? errorState.fieldMessages?.street : ""}
                onChange={(e) => onValueChange("street", e?.target?.value)}
              />
              <CustomInput
                type="text"
                placeholder={titles?.apt}
                value={accountHolderState?.apt}
                onChange={(e) => onValueChange("apt", e?.target?.value)}
              />
              <CustomInput
                type="text"
                required
                placeholder={titles?.city}
                value={accountHolderState?.city}
                error={validationTriggered && errorState.fieldErrors?.city && errorState.show}
                errorMessage={validationTriggered && errorState.fieldErrors?.city && errorState.show ? errorState.fieldMessages?.city : ""}
                onChange={(e) => onValueChange("city", e?.target?.value)}
              />
              <div className="ah_state_zip_row">
                <CustomSelectBox
                  title={titles?.state}
                  required
                  options={getAllRegionListNames(allRegionList)}
                  selectedValue={accountHolderState?.state}
                  onChange={handleStateChange}
                  className="ah_state_dropdown"
                  error={validationTriggered && errorState.fieldErrors?.state && errorState.show}
                  errorMessage={validationTriggered && errorState.fieldErrors?.state && errorState.show ? errorState.fieldMessages?.state : ""}
                />
                <CustomInput
                  type="text"
                  required
                  placeholder={titles?.zip_code}
                  value={accountHolderState?.zip_code}
                  error={validationTriggered && errorState.fieldErrors?.zip_code && errorState.show}
                  errorMessage={validationTriggered && errorState.fieldErrors?.zip_code && errorState.show ? errorState.fieldMessages?.zip_code : ""}
                  onChange={(e) => onValueChange("zip_code", e?.target?.value)}
                  className="ah_zip_input"
                />
              </div>
            </div>
          </div>

          {/* Phone Section */}
          <div className="ah_section">
            <div className="ah_section_header">
              <h3 className="ah_section_title">{titles?.phone}</h3>
              <IconQuestion
                className="ah_question_button"
                onClick={handlePhoneNumberHelp}
                {...ICON_PROPS.questionIcon}
              />
            </div>
            <p className="ah_section_description">
              {titles?.numbers_used_fo_account_management}
            </p>
            <p className="ah_section_description mb24">
              {titles?.hold_drag_item_desc}
            </p>

            {phoneList?.map((contact, index) => (
              <div
                key={contact.id}
                className="ah_phone_contact_item"
                draggable
                data-drag-index={index}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
              >
                <DragHandleIcon className="ah_drag_handle" />
                <div className="ah_phone_info">
                  <p className="ah_phone_number">{formatPhoneNumber(contact?.phoneNumber)}</p>
                  <IconEdit
                    className="ah_edit_icon"
                    {...ICON_PROPS.actionIcon}
                    onClick={() => handleEditContactClick(contact)}
                  />
                  <IconTrash
                    className="ah_delete_icon"
                    {...ICON_PROPS.actionIcon}
                    onClick={() => handleDeleteContactClick(contact)}
                  />
                </div>
              </div>
            ))}

            {phoneList?.length < 2 && !showAddContactModal && (
              <button className="ah_add_contact_link" onClick={onAddContact}>
                {titles?.add_contact}
              </button>
            )}

            {/* Inline Add/Edit Contact Form */}
            {showAddContactModal && (
              <div className="ah_inline_contact_modal">
                <AddAccountContactModal
                  onClose={handleAddContactModalClose}
                  onSave={onSaveContact}
                  phoneInfo={editingContact || newContact}
                />
              </div>
            )}
          </div>
          <CustomButton
            text={titles?.submit}
            onClick={onSubmit}
            width={"100%"}
            className="ah_submit_button"
            disabled={hasValidationErrors || showAddContactModal}
            showErrorMessage={validationTriggered && hasValidationErrors}
          />
        </div>
      </div>


    </MainLayout>
  );
};

export default AccountHolder;
